import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, TextField, Checkbox, FormControlLabel, Radio, RadioGroup, Collapse, FormGroup, IconButton, Skeleton} from '@mui/material'
import {ArrowBack} from '@mui/icons-material'
import {accountApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link, useParams} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {toSnakeCase, parseError} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton} from '../../components'

export default () => {
    const [permissionsAccess, setPermissionsAccess] = useState({})
    const [selectedPermissions, setSelectedPermissions] = useState({})
    const [isSystemRole, setIsSystemRole] = useState(false)
    const [title, setTitle] = useState('Add New Role')
    const {setLoading, setMessageBox} = useGlobalStates()
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const {roleId} = useParams()
    const [skeleton, setSkeleton] = useState(!!roleId)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                title: yup.string().required('Role Title is required'),
            }),
        ),
    })

    useEffect(() => {
        if (roleId) {
            setSkeleton(true)
            accountApi
                .getRole(roleId)
                .then(({data: {data}}) => {
                    setSkeleton(false)
                    form.setValue('title', data?.display_name)
                    setIsSystemRole(data?.is_system)
                    setTitle(data?.is_system ? 'View System Role' : 'Update Role')
                    const permissionsAccessTemp = {}
                    const selectedPermissionsTemp = {}
                    Object.keys(modulePermissions).forEach(field => {
                        if (modulePermissions[field].full_access.every(item => data?.permissions?.includes(item))) {
                            selectedPermissionsTemp[field] = true
                            permissionsAccessTemp[field] = 'full_access'
                        } else if (modulePermissions[field].read_only_access.every(item => data?.permissions?.includes(item))) {
                            selectedPermissionsTemp[field] = true
                            permissionsAccessTemp[field] = 'read_only_access'
                        }
                    })
                    setPermissionsAccess(permissionsAccessTemp)
                    setSelectedPermissions(selectedPermissionsTemp)
                })
                .catch(error => {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [])

    const modulePermissions = {
        dashboard: {
            full_access: ['dashboard.read', 'dashboard.write'],
            read_only_access: ['dashboard.read'],
        },
        client_management: {
            full_access: ['clients.read', 'clients.write'],
            read_only_access: ['clients.read'],
        },
        employee_management: {
            full_access: ['employees.read', 'employees.write'],
            read_only_access: ['employees.read'],
        },
        service_management: {
            full_access: ['services.read', 'services.write'],
            read_only_access: ['services.read'],
        },
        scheduling: {
            full_access: ['schedules.read', 'schedules.write'],
            read_only_access: ['schedules.read'],
        },
        evv: {
            full_access: ['evv.read', 'evv.write'],
            read_only_access: ['evv.read'],
        },
        billing: {
            full_access: ['billing.read', 'billing.write'],
            read_only_access: ['billing.read'],
        },
        payroll: {
            full_access: ['employees.payroll.read', 'employees.payroll.write'],
            read_only_access: ['employees.payroll.read'],
        },
        analytics: {
            full_access: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance', 'reports.custom'],
            read_only_access: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance'],
        },
        report: {
            full_access: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance', 'reports.custom'],
            read_only_access: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance'],
        },
        organization_settings: {
            full_access: ['admin.settings.read', 'admin.settings.write'],
            read_only_access: ['admin.settings.read'],
        },
        team_management: {
            full_access: ['admin.users.read', 'admin.users.write', 'admin.roles.read', 'admin.roles.write'],
            read_only_access: ['admin.users.read', 'admin.roles.read'],
        },
        integration_settings: {
            full_access: ['admin.integrations'],
            read_only_access: ['noroles'],
        },
    }

    const permissionsOptions = [
        {key: 'dashboard', label: 'Dashboard'},
        {key: 'client_management', label: 'Client Management'},
        {key: 'employee_management', label: 'Employee Management'},
        {key: 'service_management', label: 'Service Management'},
        {key: 'scheduling', label: 'Scheduling'},
        {key: 'evv', label: 'EVV'},
        {key: 'billing', label: 'Billing'},
        {key: 'payroll', label: 'Payroll'},
        {key: 'analytics', label: 'Analytics'},
        {key: 'report', label: 'Report'},
        {key: 'organization_settings', label: 'Organization Settings'},
        {key: 'team_management', label: 'Team Management'},
        {key: 'integration_settings', label: 'Integration Settings'},
    ]

    const handleCheck = key => {
        setSelectedPermissions(prev => {
            const newselectedPermissions = !prev[key]
            if (newselectedPermissions) {
                setPermissionsAccess(prevLevels => ({
                    ...prevLevels,
                    [key]: 'read_only_access',
                }))
            } else {
                setPermissionsAccess(prevLevels => {
                    const updated = {...prevLevels}
                    delete updated[key]
                    return updated
                })
            }
            return {...prev, [key]: newselectedPermissions}
        })
    }

    const handleAccessChange = (key, value) => {
        setPermissionsAccess(prev => ({...prev, [key]: value}))
    }

    const handleSave = data => {
        let permissions = []
        for (const [key, value] of Object.entries(permissionsAccess)) {
            permissions = [...permissions, ...modulePermissions[key][value]]
        }
        const roleData = {
            name: toSnakeCase(data?.title),
            display_name: data?.title,
            description: data?.title,
            permissions,
        }
        if (roleId) {
            setLoading(true)
            accountApi
                .updateRole(roleId, roleData)
                .then(() => {
                    setLoading(false)
                    setMessageBox({
                        status: 'success',
                        title: 'Role Updated!',
                        description: `${data?.title} role has been successfully updated.`,
                    })
                    navigate('/team-management/role-settings')
                })
                .catch(error => {
                    setLoading(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        } else {
            setLoading(true)
            accountApi
                .createRole(roleData)
                .then(() => {
                    setLoading(false)
                    setMessageBox({
                        status: 'success',
                        title: 'New Role Added!',
                        description: `${data?.title} role has been successfully added.`,
                    })
                    navigate('/team-management/role-settings')
                })
                .catch(error => {
                    setLoading(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to="/team-management/role-settings">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            {skeleton ? <Skeleton width={140} /> : title}
                        </Typography>
                    </div>
                    {!skeleton && !isSystemRole && (
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/team-management/role-settings')} style={{flex: 1}}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={form.handleSubmit(handleSave)} style={{flex: 1}}>
                                Save
                            </Button>
                        </div>
                    )}
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        Settings /{' '}
                        <Link className="link" to="/team-management">
                            Team Management Settings
                        </Link>
                        {' / '}
                        <Link className="link" to="/team-management/role-settings">
                            Role Settings
                        </Link>
                        {' / '}
                        {skeleton ? <Skeleton width={100} sx={{display: 'inline-block', verticalAlign: 'middle'}} /> : title}
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to="/team-management/role-settings">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                {skeleton ? <Skeleton width={180} /> : title}
                            </Typography>
                        </div>
                        {skeleton && (
                            <div className="actions">
                                <ButtonSkeleton width={86} />
                                <ButtonSkeleton width={72} />
                            </div>
                        )}
                        {!skeleton && !isSystemRole && (
                            <div className="actions">
                                <Button variant="outlined" onClick={() => navigate('/team-management/role-settings')}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={form.handleSubmit(handleSave)}>
                                    Save
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}
            <br />
            <br />
            {skeleton && (
                <SkeletonContainer>
                    <Typography variant="inputLabel" color="text.secondary">
                        Role Title{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <FieldSkeleton label={false} />
                    <br />
                    <br />
                    <Typography variant="caption" color="text.primary">
                        Permissions Access
                    </Typography>
                    <br />
                    {permissionsOptions.map(item => (
                        <div key={item.key} style={{display: 'flex', alignItems: 'center', height: 42, marginBottom: 8}}>
                            <Skeleton variant="rounded" width={18} height={18} sx={{ml: '1px', mr: '12px', borderRadius: 0.5}} />
                            <Typography variant="body1" color="text.primary">
                                {item.label}
                            </Typography>
                        </div>
                    ))}
                </SkeletonContainer>
            )}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Role Title{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Input role title"
                    fullWidth
                    disabled={isSystemRole}
                    {...form.register('title')}
                    error={!!form.formState.errors['title']}
                    helperText={form.formState.errors['title']?.message}
                />
                <br />
                <br />
                <Typography variant="caption" color="text.primary">
                    Permissions Access
                </Typography>
                <br />
                <FormGroup>
                    {permissionsOptions.map(item => (
                        <div key={item.key} style={{marginBottom: 8}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!selectedPermissions[item.key]}
                                        onChange={() => handleCheck(item.key)}
                                        disabled={isSystemRole}
                                    />
                                }
                                label={item.label}
                            />
                            <Collapse in={selectedPermissions[item.key]} timeout="auto" unmountOnExit>
                                <RadioGroup
                                    row
                                    value={permissionsAccess[item.key] || 'full'}
                                    onChange={e => handleAccessChange(item.key, e.target.value)}
                                    style={{marginLeft: 32}}>
                                    <FormControlLabel value="read_only_access" control={<Radio />} label="Read only access" disabled={isSystemRole} />
                                    <FormControlLabel value="full_access" control={<Radio />} label="Full access" disabled={isSystemRole} />
                                </RadioGroup>
                            </Collapse>
                        </div>
                    ))}
                </FormGroup>
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
